/**
 * Renders the form used to reply to notes
 */

import Backbone from 'backbone'

import template from './reply-form.hbs'
import momentHelper from 'scoreboard/components/moment-helper'
import i18n from 'scoreboard/components/i18n'
import { Note } from 'scoreboard/notes/notes'
import calendars from 'scoreboard/components/calendars'
import _ from 'lodash'
import NoteForm from './note-form'
import appDisplay from 'scoreboard/components/app-display'
import BusyButton from 'scoreboard/components/busy-button'
import user from 'scoreboard/components/user'
import DeleteConfirmation from 'scoreboard/components/delete-confirmation'
import $ from 'jquery'
import TextArea from 'scoreboard/components/textarea'
import escapePreserveLineBreaks from 'scoreboard/components/escape-preserve-line-breaks'
import htmlToText from 'scoreboard/components/html-to-text'
import permissions from 'scoreboard/components/permissions'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import unwrapModel from 'scoreboard/components/unwrap-model'
import guard from 'scoreboard/components/unsaved/guard'
import conductor from 'scoreboard/components/conductor'
import GuardConstants from 'scoreboard/components/unsaved/guard-constants'
const { HIDE_WARNING } = GuardConstants
import icon from 'scoreboard/components/icon'
import urlsToLinks from 'scoreboard/components/urls-to-links'
import layoutManager from 'scoreboard/components/layout-manager'
import DateTooltips from 'scoreboard/components/date-tooltips'

var ReplyForm = Backbone.View.extend({

	className: 'form note-reply-form',

	events: {
		// edit existing top-level note
		'click .edit': 'editNote',

		// show new reply form
		'click .add-reply': 'showNewReplyForm',
		// save new reply
		'click .new-reply .save': 'saveNewReply',
		'click .new-reply .cancel': 'cancelNewReply',
		// toggle reply button for new replies
		'keyup .new-reply textarea': 'toggleNewReplyButtons',

		// click on name of a domain object
		'click .appliesTo.domain-object-label': 'onClickDomainObjectLabel',

		// EVENTS FOR ALREADY-CREATED REPLIES

		// edit a reply
		'click .edit-reply': 'onClickEditReply',
		'click .delete-reply': 'onClickDeleteReply',
		'click .reply .save': 'saveExistingReply',
		'click .reply .cancel': 'cancelEditReply'
	},

	/**
	 * Renders a note, its replies, and controls to edit/delete the note and
	 * add/edit/delete its replies
	 *
	 * @param {object} options
	 *
	 * @param {Note} options.model
	 * the model for the note
	 *
	 * @param {ScorecardNode|Initiative} options.rootDomainObject
	 * the model for the parent scorecard or initiative that this note is being
	 * displayed for; may be different from opt.noteDomainObject if
	 * descendant notes are being shown
	 *
	 * @param {ScorecardNode|Initiative} options.noteDomainObject
	 * the model for the scorecard or initiative that this note is associated with
	 *
	 * @param {boolean} [options.export=false]
	 * whether the view is being rendered for exports; allows things like turning relative times
	 * (1 hour ago) to absolute times (Nov 20, 2019 8:51 AM)
	 *
	 * @param {boolean} [options.alwaysShowReplyForm=false]
	 * true if the textarea for entering a new reply should always be showing;
	 * false if there should be an Add Reply button instead
	 *
	 * @param {boolean} [options.showReplyIcon=false]
	 * true if the note icon should appear beside the reply count; false if not
	 *
	 * @param {boolean} [options.alwaysShowDomainObject=false]
	 * true if options.noteDomainObject's label should be shown even if
	 * options.noteDomainObject == options.rootDomainObject
	 *
	 * @param {Boolean} [opt.disabled=false]
	 * indicates whether interactions should be disabled
	 * (ie whether clicking on notes pops up the dialog)
	 *
	 * @param {Boolean} [opt.guard=true] Pass false to prevent any guards from
	 * being applied (e.g. the reply form)
	 */
	initialize: function(options = {}) {
		this.options = _.extend({guard: true}, options)
		this.on('replies:add replies:destroy', this._repliesChanged)
	},

	render: function() {
		this.$el.empty().append(template(this.context()))
		this.$('.appliesTo.date').text(appliesTo(this.model, this.model.section))

		let showDomainObjectLabel = false
		if (this.options.alwaysShowDomainObject) {
			showDomainObjectLabel = true
		} else {
			// If the note is attached to a different domain object than the one in the dialog (ie a descendant of
			// the node displayed in the dashboard), display the title of the note's actual domain object
			if (this.model.get('domainId') != this.options.rootDomainObject.id && this.options.noteDomainObject) {
				showDomainObjectLabel = true
			}
		}

		if (showDomainObjectLabel) {
			this.$('.appliesTo.domain-object-label')
				.text(unwrapModel(this.options.noteDomainObject).label)
		}

		this.secure()

		if (this.canCreateReplies()) {
			this.newReplyTextArea = new TextArea({el: this.$('.new-reply textarea')})

			this.listenTo(this.newReplyTextArea, 'resize', () => {
				this.trigger('resize')
			})
		}

		this.toggleNewReplyForm(this.options.alwaysShowReplyForm)

		// Render is re-called each time a new reply is entered, so refresh our tooltips
		this._covertButtonTooltips && this._covertButtonTooltips.cleanup()
		this._covertButtonTooltips = new CovertButtonTooltips({ el: this.el })

		// #50109 provide a tooltip when hovering over date description to
		// display the full date
		_.invokeMap([this._dateTooltips], 'cleanup')
		this._dateTooltips = new DateTooltips({ el: this.el })

		this.scrollContainer = $(this.options.scrollContainer || this.$('.dialog-body'))

		this._setupGuard()

		this._markHasReplies()

		return this
	},

	_repliesChanged: function() {
		this.$('.replies').toggleClass('has-replies', this.context().replies.length > 0)
		this.$('.replies-message').text(this.repliesMsg())
		// If there was a reply icon visible, we'll have to re-insert it into the
		// .replies-message here
		if (this.options.showReplyIcon) {
			this.$('.replies-message').prepend(icon('note'))
		}
	},

	/**
	 * If a user can interact with the note and author a reply then they
	 * should be presented with a guard when navigating away. (#23464)
	 */
	_setupGuard: function() {
		if (this.options.guard) { // #24963 - note wobs are disabled
			this.guard = guard.guard(() => {
				return !_.isEmpty(this.$('.is-replying, .is-editing .save:not([disabled])'))
			}, () => {
				let unsavedForms = this.$('.is-replying, .is-editing .save:not([disabled])')
				if (unsavedForms.length == 1) {
					if (unsavedForms.hasClass('is-replying')) {
						return this.saveNewReply()
					} else {
						return this.saveExistingReply(unsavedForms)
					}
				} else {
					return HIDE_WARNING
				}
			})
		}
	},

	secure: function() {
		let noReplies = !(this.model.get('replies') || []).length

		// secure the new reply form
		if (this.canCreateReplies()) {
			this.$el.addClass('can-reply')
		} else {
			this.$('.new-reply').remove()
			this.$el.toggleClass('no-permission', noReplies)
		}

		this.$el.toggleClass('no-replies', noReplies)
		function cannotEdit (note, useSourceNotes) {
			const {update} = note.attributes
			if (layoutManager.sharedResource) {
				// #28443 always show buttons on shared dashboard
				return false
			} else if (useSourceNotes === true) {
				// #32577 don't allow editing from linked node that is using source notes
				return true
			} else {
				return !update
			}
		}

		// secure the edit note button
		if (cannotEdit(this.model, this.options.useSourceNotes)) {
			this.$('.note-actions').remove()
		}

		// secure the edit/delete reply buttons
		this.model.replies()
			.filter(cannotEdit)
			.forEach(reply => {
				this.replyElFromId(reply.id).find('.note-controls').remove()
			})
	},

	onClickDomainObjectLabel: function() {
		guard.conditionallyRun(() => {
			conductor.navigate(`${this.model.section}/${this.model.get('domainId')}`, { trigger: true})
		})
	},

	canCreateReplies() {
		var permission = this.model.get('periodId') ? 'createPeriodNotes' : 'createGlobalNotes'
		return this.options.rootDomainObject.get(permission) && permissions.isEditableIfReplicated(this.model)
	},

	// builds the context we pass to our Handlebars template
	context: function() {
		var replies = _.cloneDeep(this.model.get('replies') || [])

		replies = _.sortBy(replies, function(note) {
			return note.created
		})

		_.each(replies, function(reply) {
			reply.displayTime = createdAndModifiedMessage(reply, this.options.export, false)
			reply.update = reply.update || layoutManager.sharedResource
			if (!reply.html) {
				reply.text = urlsToLinks(escapePreserveLineBreaks(reply.text))
			}
		}.bind(this))

		var text = this.model.get('text')
		if (!this.model.get('html')) {
			text = urlsToLinks(escapePreserveLineBreaks(text))
		}

		return {
			alwaysShowReplyForm: this.options.alwaysShowReplyForm,
			lastModifier: this.model.get('lastModifier'),
			postedText: createdAndModifiedMessage(this.model, this.options.export, true),
			created: _.get(this.model.attributes, 'created'),
			text: text,
			replies: replies,
			repliesMsg: this.repliesMsg(),
			showReplyIcon: this.options.showReplyIcon
		}
	},

	repliesMsg: function() {
		var numReplies = (this.model.get('replies') || []).length
		var key
		if (this.options.showReplyIcon) {
			// Per #20774, the replies text is slightly different for dashboard widgets
			// https://github.com/SpiderStrategies/Scoreboard/issues/20774#issuecomment-443688505
			key = 'dashboards.display.noteReplies.withAppendedArg'
		} else {
			if (numReplies == 1) {
				key = 'dashboards.display.noteReply.withArg'
			} else {
				key = 'dashboards.display.noteReplies.withArg'
			}
		}
		return i18n.get(key, numReplies)
	},

	saveNewReply: function() {
		new BusyButton({ el: this.$('.save') }).loading()
		var note = new Note({
			parentId: this.model.id,
			domainId: this.model.get('domainId'),
			text: this.newReplyTextArea.$el.val()
		})
		note.section = this.model.section
		return note.save({}, {
			success: (reply, response, options) => {
				reply.set('lastModifier', user.impersonatorOrUser)
				var content = this.$('.dialog-body')
				var styles = content.css(['max-height', 'margin-top', 'margin-bottom'])

				this.model.setReply(reply)
				this.render()

				// Since we've just re-rendered everything, we've lost the styling
				// for how big the .dialog-body content box is. Apply that sizing
				this.$('.reply').closest('.dialog-body').css(styles)

				this.trigger('replies:add', {
					reply,
					el: this.$('.reply').last()[0]
				})

				this.trigger('resize')
			}
		})
	},

	// returns the reply element corresponding to the given event
	replyEl: function(e) {
		// support a DOM event, or a passed in DOM element
		e = e.currentTarget || e
		return this.$(e).closest('.reply')
	},

	replyElFromId: function(replyId) {
		return this.$(`.reply[data-id='${replyId}']`)
	},

	replyElFromEventOrId: function(eventOrReplyId) {
		return _.isFinite(parseInt(eventOrReplyId)) ?
			this.replyElFromId(eventOrReplyId) :
			this.replyEl(eventOrReplyId)
	},

	onClickDeleteReply: function(e) {
		if (layoutManager.sharedResource) {
			this.deleteNoteReplyDrillDown(this.replyEl(e).data('id'))
		} else {
			this.deleteReply(e)
		}
	},

	deleteReply: function(eventOrReplyId) {
		const replyEl = this.replyElFromEventOrId(eventOrReplyId)
		var id = replyEl.data('id')
		var confirmation = new DeleteConfirmation({
			description: i18n.get('scorecards.notes.reply').toLowerCase()
		})
		appDisplay.dialog(confirmation)
		confirmation.once('confirm', function(group) {
			this.model.deleteReply(id, this.options.rootDomainObject)
			confirmation.trigger('done')
			// remove reply from display
			replyEl.remove()
			// update number of replies message
			this.$('.replies-message label').text(this.repliesMsg())
			this.trigger('replies:destroy')
			this.trigger('resize')
			this._markHasReplies()
		}.bind(this))
	},

	_markHasReplies: function() {
		this.$('.replies-container')
			.toggleClass('has-replies', _.result(this.model.get('replies'), 'length', 0) > 0)
	},

	onClickEditReply: function(e) {
		e.preventDefault()
		e.stopPropagation()

		if (layoutManager.sharedResource) {
			this.editNoteReplyDrillDown(this.replyEl(e).data('id'))
		} else {
			this.editReply(e)
		}
	},

	/**
	 * @param {Event|Number} eventOrReplyId
	 */
	editReply: function(eventOrReplyId) {
		const replyEl = this.replyElFromEventOrId(eventOrReplyId)

		// hide reply button
		this.$('reply').hide()

		// insert current text in textarea
		var text = this.model.replies().get(replyEl.data('id')).get('text')
		// replace the old texarea to avoid previously edited text from reappearing
		replyEl.find('textarea').replaceWith('<textarea>')
		var textarea = replyEl.find('textarea')
		textarea.html(htmlToText(text))

		// hides edit and delete buttons and shows save/cancel buttons
		replyEl.addClass('is-editing')

		// the 'save' button should appear disabled until a change has been made
		// to the reply text
		var saveButton = replyEl.find('.save')[0]
		saveButton.disabled = true
		textarea.on('input', () => saveButton.disabled = false)
		this.editReplyTextArea = new TextArea({el: textarea}).render()
		this._markHasReplies()
	},

	saveExistingReply: function(e) {
		var replyEl = this.replyEl(e)

		var note = new Note({
			id: replyEl.data('id'),
			parentId: this.model.id,
			domainId: this.model.get('domainId'),
			created: this.model.get('created'),
			text: replyEl.find('textarea').val()
		}, {
			section: this.model.section
		})
		return note.save({}, {
			success: function(reply, response, options) {
				this.model.setReply(reply)

				// update displayed text content with new text
				var replyEl = this.replyEl(e)
				var textEl = replyEl.find('.reply-content-text')
				textEl.html(escapePreserveLineBreaks(reply.get('text')))

				// hide save/cancel buttons and shows edit/delete buttons AFTER calling this.replyEl(e)
				replyEl.removeClass('is-editing')

				replyEl.find('.reply-content-posted')
					.text(createdAndModifiedMessage(reply, this.options.export, false))

				this._markHasReplies()
			}.bind(this)
		})
	},

	cancelEditReply(e) {
		// So the dialog isn't auto closed
		e.stopPropagation()

		this.replyEl(e).removeClass('is-editing')
	},

	showNewReplyForm () {
		if (layoutManager.sharedResource) {
			this.viewNoteDrillDown()
			return
		}

		this.toggleNewReplyForm(true)
	},

	toggleNewReplyForm (show) {
		this.$('.new-reply').toggleClass('is-not-displayed', !show)
		this.$('.add-reply').prop('disabled', show)
		this.toggleNewReplyButtons()
		if (show) {
			this.newReplyTextArea && this.newReplyTextArea.el.focus()
		}
	},

	// show the reply button if and only if there is text in the new reply box
	toggleNewReplyButtons: function() {
		var text = this.newReplyTextArea ? this.newReplyTextArea.$el.val() : ''
		text = $.trim(text)
		var showReply = !!(text && text.length > 0)

		// show reply/cancel buttons if needed
		this.$('.new-reply button').toggleClass('is-not-displayed', !showReply)
		this.$('.new-reply .save').toggle(showReply)
		this.$('.new-reply').toggleClass('is-replying', showReply)
	},

	cancelNewReply(e) {
		this.newReplyTextArea.$el.val('')
		this.toggleNewReplyForm(!!this.options.alwaysShowReplyForm)

		// Prevent 'cancel' click from closing the dialog
		e.stopPropagation()
	},

	editNote: function() {
		if (layoutManager.sharedResource) {
			this.editNoteDrillDown()
			return
		}

		var noteForm = new NoteForm({
			model: this.model,
			domainObject: this.options.rootDomainObject
		})
		appDisplay.dialog(noteForm)
		noteForm.once('done', function() {
			this.render()
			this.trigger('edit')
			this.trigger('resize')
		}.bind(this))
		this.model.once('destroy', _.bind(this.trigger, this, 'done'))
	},

	baseNoteUrl: function() {
		const domainId = this.model.attributes.domainId
		return `${this.model.section}/${domainId}/note/${this.model.id}`
	},

	editNoteDrillDown: function() {
		conductor.navigate(`${this.baseNoteUrl()}/edit`)
	},

	editNoteReplyDrillDown: function(replyId) {
		conductor.navigate(`${this.baseNoteUrl()}/reply/${replyId}/edit`)
	},

	deleteNoteReplyDrillDown: function(replyId) {
		conductor.navigate(`${this.baseNoteUrl()}/reply/${replyId}/delete`)
	},

	//Used for adding a reply to a note because a new reply form
	//is automatically opened when viewing a note
	viewNoteDrillDown: function() {
		conductor.navigate(`${this.baseNoteUrl()}`)
	},

	remove() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this._covertButtonTooltips, this.newReplyTextArea,
			this._dateTooltips, this.editReplyTextArea], 'remove')
		this.guard && guard.removeGuard(this.guard)
	}

})

/**
 * Returns a message appropriate for the notes created and modified dates.
 *
 * @param note
 * 			the note; can be a plain object or a Model
 *
 * @param isExport
 * 			boolean indicating if export formatting should be used
 * 			(ie absolute dates instead of relative dates)
 *
 * @param postedPrefix
 * 			whether the message should be prefixed with "Posted "
 */
function createdAndModifiedMessage (note, isExport, postedPrefix) {
	note = unwrapModel(note)

	let created = isExport
		? momentHelper.formatDateTime(note.created)
		: momentHelper.fromNow(note.created)

	let message = postedPrefix
		? i18n.get('forums.topicPost.posted.withArg', created)
		: created

	if (note.lastModified) {
		let modified = momentHelper.difference(note.created, note.lastModified)

		message += ', ' + i18n.get('forums.topicPost.updatedLater', modified)
	}

	return message
}

/**
 * Given a note and the corresponding domain object's section, returns descriptive
 * text of the period/date that the note applies to
 */
function appliesTo (note, section) {
	note = unwrapModel(note)

	if (section === 'scorecards') {
		var periodId = note.periodId
		return periodId ? calendars.periodsById[periodId].label : i18n.get('common.allTime')
	} else if (section === 'initiatives') {
		return momentHelper.formatDate(note.appliesTo) || i18n.get('common.allTime')
	}
}

export default ReplyForm
export { appliesTo }
ReplyForm.__filename__ = 'notes/reply-form.js'
